import React from 'react';
import { Alert, Button, Form, Input, InputNumber, Select } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import { PgProviderAccountType, PgProviderRoutesMaster } from '../../../../lib/api/pgProviderRoute';
import { getAccountTypeLabel, PG_PROVIDER_ACCOUNT_TYPE_LABEL } from '../../constants/labels';
import { EditableRoute } from '../../types';

type RouteEditorProps = {
  route: EditableRoute;
  master: PgProviderRoutesMaster;
  disabled: boolean;
  onChange: (patch: Partial<EditableRoute>) => void;
  onRemove: () => void;
  onClose: () => void;
};

export const RouteEditor = (props: RouteEditorProps): React.JSX.Element => {
  const { route, master, disabled, onChange, onRemove, onClose } = props;
  const requiredTypes: PgProviderAccountType[] = route.accountType ? [route.accountType] : ['general', 'liquidity'];
  const accounts = requiredTypes.map((type) => ({
    type,
    account: master.pgProviderAccounts.find(
      (item) => item.pgProviderId === route.pgProviderId && item.accountType === type,
    )?.account,
  }));
  return (
    <div className="rule-editor">
      <Form layout="vertical" component="div" disabled={disabled}>
        <div className="settings-form-grid">
          <Form.Item label="결제수단">
            <Select
              aria-label="규칙 결제수단"
              value={route.payMethodId}
              options={master.payMethods.map((item) => ({ value: item.id, label: item.name }))}
              onChange={(value) => onChange({ payMethodId: value })}
            />
          </Form.Item>
          <Form.Item label="계정 유형">
            <Select
              aria-label="규칙 계정 유형"
              value={route.accountType ?? 'any'}
              options={[
                { value: 'any', label: '전체' },
                ...Object.entries(PG_PROVIDER_ACCOUNT_TYPE_LABEL).map(([value, label]) => ({ value, label })),
              ]}
              onChange={(value) => onChange({ accountType: value === 'any' ? null : (value as PgProviderAccountType) })}
            />
          </Form.Item>
          <Form.Item label="결제 금액 상한" extra="비우면 금액 제한 없이 적용합니다.">
            <InputNumber
              controls={false}
              aria-label="결제 금액 상한"
              value={route.maxAmount}
              min={1}
              max={4294967295}
              precision={0}
              placeholder="제한 없음"
              addonAfter="원 이하"
              formatter={(value) => String(value ?? '').replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={(value) => Number((value ?? '').replace(/,/g, ''))}
              onChange={(value) => onChange({ maxAmount: value })}
            />
          </Form.Item>
          <Form.Item label="연결 PG">
            <Select
              aria-label="연결 PG"
              value={route.pgProviderId}
              options={master.pgProviders.map((item) => ({ value: item.id, label: item.korName }))}
              onChange={(value) => onChange({ pgProviderId: value })}
            />
          </Form.Item>
          <Form.Item label="운영 메모" className="full-width">
            <Input.TextArea
              aria-label="운영 메모"
              value={route.memo ?? ''}
              maxLength={500}
              autoSize={{ minRows: 2, maxRows: 4 }}
              onChange={(event) => onChange({ memo: event.target.value || null })}
            />
          </Form.Item>
        </div>
        {accounts.some((item) => !item.account) && (
          <Alert type="error" showIcon message="선택한 PG에 필요한 계정이 없습니다. PG 또는 계정 유형을 확인하세요." />
        )}
        <details className="rule-account">
          <summary>연결 계정(MID) 확인</summary>
          {accounts.map((item) => (
            <div key={item.type}>
              {getAccountTypeLabel(item.type)}: {item.account ?? '미등록'}
            </div>
          ))}
        </details>
      </Form>
      <div className="rule-editor-actions">
        <Button danger type="text" icon={<DeleteOutlined />} disabled={disabled} onClick={onRemove}>
          규칙 삭제
        </Button>
        <Button onClick={onClose}>편집 닫기</Button>
      </div>
    </div>
  );
};
